import { v4 as uuidv4 } from 'uuid';
import { Loan, loanStatusDescriptionRemapping, LOAN_TYPE_GROUP_MAP } from '@/types/nsldsTypes';
import { processLoanEvents } from './event-parser'; // Import event parser
import { parse, isValid } from 'date-fns'; // Import date-fns functions

/**
 * Safely parses a string value into a float, handling potential errors.
 * Returns 0 if parsing fails or the input is invalid.
 */
function safeParseFloat(value: string | undefined | null): number {
  if (value === null || value === undefined) {
    return 0;
  }
  const cleanedValue = value.replace(/[^0-9.-]/g, '');
  const numericValue = parseFloat(cleanedValue);
  return isNaN(numericValue) ? 0 : numericValue;
}

/**
 * Parses the lines corresponding to a single loan section.
 *
 * @param loanLines - The block of lines for one loan.
 * @param borrower - The owner of the loan ('user' or 'partner').
 * @param fileEndDate - The effective end date for calculations.
 * @returns The parsed Loan object.
 * @throws Error if event section structure is invalid or boundaries are unclear.
 */
export function processLoanSection(
  loanLines: string[],
  borrower: 'user' | 'partner',
  fileEndDate: Date
): Loan {
  const loanObject: Partial<Loan> = { Borrower: borrower };
  const eventLines: string[] = [];
  let mostRelevantContactName: string | null = null;
  let foundMostRelevantContact = false;
  let repaymentBeginDate: Date | null = null; // Variable to store parsed date
  let currentLoanStatus: string | null = null; // Store current loan status code
  let repaymentPlanDesc: string | null = null; // Store repayment plan description

  let eventSectionStartIndex = -1;
  let eventSectionEndIndex = -1; // Exclusive end index
  let lastEventDateIndex = -1; // Keep track of the last 'Loan Status Effective Date'
  let foundNonEventKeyAfterEvents = false;

  // --- Pass 1: Find Event Section Boundaries ---
  for (let i = 0; i < loanLines.length; i++) {
    const trimmedLine = loanLines[i].trim();
    if (!trimmedLine) continue; // Skip empty lines
    const separatorIndex = trimmedLine.indexOf(':');
    if (separatorIndex === -1) continue; // Skip lines without ':'
    const key = trimmedLine.substring(0, separatorIndex).trim();

    // Detect start of event section
    if (eventSectionStartIndex === -1 && key === 'Loan Status') {
      eventSectionStartIndex = i;
      lastEventDateIndex = -1; // Reset marker
      foundNonEventKeyAfterEvents = false; // Reset marker
    }

    // If we are potentially inside an event section
    else if (eventSectionStartIndex !== -1 && !foundNonEventKeyAfterEvents) {
      if (key === 'Loan Status Effective Date') {
        lastEventDateIndex = i;
      } else if (key === 'Loan Status' || key === 'Loan Status Description') {
        // Still within the expected event keys, continue
      } else {
        // Found a key that's not part of the event structure
        if (lastEventDateIndex === -1) {
          // Found a non-event key but never saw an effective date after 'Loan Status' started
          throw new Error(
            `Loan ${
              loanObject['Loan Award ID'] ?? 'Unknown'
            }: Invalid event section structure. Found '${key}' after 'Loan Status' (line ${String(
              eventSectionStartIndex
            )}) but before any 'Loan Status Effective Date'.`
          );
        }
        // This key marks the end of the event block.
        eventSectionEndIndex = lastEventDateIndex + 1;
        foundNonEventKeyAfterEvents = true;
        // Do not break here; continue processing the rest of the lines in Pass 2 logic later
      }
    }
  }

  // --- Validate Event Section Boundaries ---
  // Case 1: Event section started, but loop finished before a non-event key was found *after* an effective date
  if (eventSectionStartIndex !== -1 && !foundNonEventKeyAfterEvents && lastEventDateIndex !== -1) {
    // This means the loan block ended while still in the event section. Define end as last date + 1.
    eventSectionEndIndex = lastEventDateIndex + 1;
    foundNonEventKeyAfterEvents = true; // Mark as logically ended
  }
  // Case 2: Event section started, but no 'Loan Status Effective Date' was ever found.
  else if (eventSectionStartIndex !== -1 && lastEventDateIndex === -1) {
    throw new Error(
      `Loan ${
        loanObject['Loan Award ID'] ?? 'Unknown'
      }: Invalid event section. Started at line ${String(
        eventSectionStartIndex
      )} but no 'Loan Status Effective Date' found before end of loan block.`
    );
  }
  // Case 3: Event section never started (eventSectionStartIndex === -1). This is valid, means no events.

  // Extract event lines if a valid section was identified
  if (eventSectionStartIndex !== -1 && eventSectionEndIndex !== -1) {
    eventLines.push(...loanLines.slice(eventSectionStartIndex, eventSectionEndIndex));
  }

  // --- Pass 2: Process all lines for loan details (excluding event lines logic here) ---
  for (let i = 0; i < loanLines.length; i++) {
    // Skip lines that were identified as part of the event block
    if (
      eventSectionStartIndex !== -1 &&
      eventSectionEndIndex !== -1 &&
      i >= eventSectionStartIndex &&
      i < eventSectionEndIndex
    ) {
      continue;
    }

    const trimmedLine = loanLines[i].trim();
    const separatorIndex = trimmedLine.indexOf(':');
    if (separatorIndex === -1 || !trimmedLine) continue;

    const key = trimmedLine.substring(0, separatorIndex).trim();
    const value = trimmedLine.substring(separatorIndex + 1).trim();

    // --- Parse Loan Key-Value Pairs ---
    switch (key) {
      case 'Loan Award ID':
        loanObject['Loan Award ID'] = value ? value.trim() : uuidv4();
        break;
      case 'Loan Type Code':
        loanObject['Loan Type Code'] = value;
        loanObject['Loan Type Group'] = LOAN_TYPE_GROUP_MAP[value] || 'Other';
        break;
      case 'Loan Type Description':
        loanObject['Loan Type Description'] = value;
        break;
      case 'Loan Date':
        loanObject['Loan Date'] = value;
        break;
      case 'Loan Repayment Begin Date': {
        const parsedRepayBeginDate = parse(value, 'MM/dd/yyyy', new Date());
        if (isValid(parsedRepayBeginDate)) {
          repaymentBeginDate = parsedRepayBeginDate;
        } else {
          // Optionally throw an error if the date is invalid but present
          // console.warn(`Loan ${loanObject['Loan Award ID'] ?? 'Unknown'}: Invalid Loan Repayment Begin Date format: ${value}`);
        }
        break;
      }
      case 'Loan Attending School Name':
        loanObject['Loan Attending School Name'] = value;
        break;
      case 'Loan Amount':
        loanObject['Loan Amount'] = safeParseFloat(value);
        break;
      case 'Loan Interest Rate': {
        const rateString = value.replace('%', '').trim();
        loanObject['Loan Interest Rate'] = safeParseFloat(rateString) / 100;
        break;
      }
      case 'Current Loan Status':
        currentLoanStatus = value; // Store the status code
        loanObject['Current Loan Status'] = value;
        break;
      case 'Current Loan Status Description':
        loanObject['Current Loan Status Description'] =
          loanStatusDescriptionRemapping[value] || value;
        break;
      case 'Loan Repayment Plan Type Code Description':
        repaymentPlanDesc = value; // Store description
        break;
      // Balance fields (handle specific patterns)
      case 'Loan Outstanding Principal Balance':
      case 'Outstanding Principal':
        loanObject['Outstanding Principal'] = safeParseFloat(value);
        break;
      case 'Loan Outstanding Interest Balance':
      case 'Outstanding Interest':
        loanObject['Outstanding Interest'] = safeParseFloat(value);
        break;
      case 'Loan PSLF Cumulative Matched Months':
        loanObject['Loan PSLF Cumulative Matched Months'] = safeParseFloat(value) || 0;
        break;
      case 'Consolidation Loan With Any Parent Plus Indicator':
        loanObject['Consolidation Loan With Any Parent Plus Indicator'] =
          value.toLowerCase() === 'yes' ? 'Yes' : value.toLowerCase() === 'no' ? 'No' : null;
        break;

      // Handle Contact Name - looking for the 'Most Relevant' one
      case 'Loan Contact Name':
        mostRelevantContactName = value;
        // Tentatively assign, will be overwritten if 'Most Relevant: Yes' is found later
        if (!foundMostRelevantContact) {
          loanObject['Loan Contact Name'] = value;
        }
        break;
      case 'Most Relevant':
        if (value.toLowerCase() === 'yes' && mostRelevantContactName) {
          loanObject['Loan Contact Name'] = mostRelevantContactName;
          foundMostRelevantContact = true;
        } else if (value.toLowerCase() === 'yes' && !mostRelevantContactName) {
          // This indicates a structural issue if 'Most Relevant: Yes' appears before the name
          console.warn(
            // Keep warning for this specific edge case
            `Loan ${
              loanObject['Loan Award ID'] ?? 'Unknown'
            }: Found 'Most Relevant: Yes' before 'Loan Contact Name'. Contact name might be missing or out of order.`
          );
          foundMostRelevantContact = true;
        }
        break;
      // Ignore event keys if they somehow slip through (shouldn't happen with the skip logic)
      // Note: These cases are technically not needed due to the continue statement above,
      // but kept defensively.
      case 'Loan Status':
      case 'Loan Status Description':
      case 'Loan Status Effective Date':
        console.error(
          `Loan ${
            loanObject['Loan Award ID'] ?? 'Unknown'
          }: Error - Event key '${key}' processed outside event block boundary logic.`
        );
        // Potentially throw here if this indicates a major logic flaw
        break;
      // Default case to handle other ignored keys if needed
      // default:
      //   console.log(`Ignoring key: ${key}`);
      //   break;
    }
  }

  // --- Post-Processing and Validation --- //

  // Ensure required fields have default values before event processing
  loanObject['Loan Award ID'] = loanObject['Loan Award ID'] || uuidv4(); // Ensure ID exists
  loanObject['Loan Type Group'] = loanObject['Loan Type Group'] || 'Unknown';
  loanObject['Current Loan Status Description'] =
    loanObject['Current Loan Status Description'] || 'Unknown Status';
  loanObject['Outstanding Principal'] = loanObject['Outstanding Principal'] ?? 0;
  loanObject['Outstanding Interest'] = loanObject['Outstanding Interest'] ?? 0;
  loanObject['Total Outstanding'] =
    (loanObject['Outstanding Principal'] ?? 0) + (loanObject['Outstanding Interest'] ?? 0);
  loanObject['Loan Interest Rate'] = loanObject['Loan Interest Rate'] ?? 0;
  loanObject['Loan PSLF Cumulative Matched Months'] =
    loanObject['Loan PSLF Cumulative Matched Months'] ?? 0;
  loanObject['Consolidation Loan With Any Parent Plus Indicator'] =
    loanObject['Consolidation Loan With Any Parent Plus Indicator'] ?? null;
  loanObject.qualifiedPaymentMonths = 0; // Initialize before event processing
  // Set payment plan and repayment status based on parsed values
  loanObject.isInRepayment = currentLoanStatus === 'RP'; // Set based on status code RP
  loanObject['Current Payment Plan'] = loanObject.isInRepayment ? repaymentPlanDesc : null; // Assign description only if in repayment
  // Set default contact name if none was found or marked relevant
  loanObject['Loan Contact Name'] = loanObject['Loan Contact Name'] || 'N/A';

  // Process Events if any were found
  if (eventLines.length > 0 && loanObject['Loan Award ID']) {
    const { qualifiedPaymentMonths: calculatedMonths } = processLoanEvents(
      eventLines,
      loanObject['Loan Award ID'],
      fileEndDate,
      repaymentBeginDate // Pass the parsed date
    );
    loanObject.qualifiedPaymentMonths = calculatedMonths;
  } else if (
    eventSectionStartIndex !== -1 &&
    eventSectionEndIndex !== -1 &&
    eventLines.length === 0
  ) {
    console.warn(
      `Loan ${
        loanObject['Loan Award ID'] ?? 'Unknown'
      }: Identified event section boundaries (lines ${String(eventSectionStartIndex)}-${String(
        eventSectionEndIndex - 1
      )}) but extracted zero event lines. Check boundary logic.`
    );
  }

  // Final validation of the constructed Loan object
  if (
    !loanObject['Loan Award ID'] ||
    !loanObject['Loan Type Group'] ||
    !loanObject['Current Loan Status Description']
  ) {
    console.error(
      'Loan object missing essential fields after processing:',
      loanObject,
      'Section lines:',
      loanLines.slice(0, 5)
    );
    throw new Error(`Loan ${loanObject['Loan Award ID']}: Failed to parse essential loan fields.`);
  }

  // Ensure all required fields are present and of correct type
  const validatedLoan: Loan = {
    ...loanObject,
    'Loan Award ID': loanObject['Loan Award ID'] ?? '',
    'Loan Type Group': loanObject['Loan Type Group'] ?? 'Unknown',
    'Current Loan Status Description': loanObject['Current Loan Status Description'] ?? 'Unknown',
    'Loan Interest Rate': loanObject['Loan Interest Rate'] ?? 0,
    'Outstanding Principal': loanObject['Outstanding Principal'] ?? 0,
    'Outstanding Interest': loanObject['Outstanding Interest'] ?? 0,
    'Total Outstanding': loanObject['Total Outstanding'] ?? 0,
    'Loan PSLF Cumulative Matched Months': loanObject['Loan PSLF Cumulative Matched Months'] ?? 0,
    'Consolidation Loan With Any Parent Plus Indicator':
      loanObject['Consolidation Loan With Any Parent Plus Indicator'] ?? null,
    Borrower: loanObject.Borrower ?? 'user',
    qualifiedPaymentMonths: loanObject.qualifiedPaymentMonths ?? 0,
    isInRepayment: loanObject.isInRepayment ?? false,
    'Current Payment Plan': loanObject['Current Payment Plan'] ?? null,
    'Loan Contact Name': loanObject['Loan Contact Name'] ?? 'N/A',
    originalPrincipal: loanObject.originalPrincipal || loanObject['Outstanding Principal'] || 0,
    degreeType: loanObject.degreeType ?? 'graduate',
  };

  return validatedLoan;
}
